import round from 'lodash/round'
import range from 'lodash/range'
import dateFormat from 'dateformat'
import type { Router } from 'vue-router'
import type { FishingRegion, FishStockingRecord } from '@onxmaps/supergraph'

import routeNames from '@/fish/router/constants'
import { formatNumber, metersToFeet } from '@/components/shared/x-distance.vue'

import { getConversionFunction } from '@/lib/area'
import { AreaAbbrevs } from '@/lib/area/types'
import { getItem } from '@/plugins/storage'

import pubsub from '@/lib/pubsub'
import { MARKUP_CREATE_ORIGINS } from '@/lib/enums-marketing'
import { queryCoord } from '@/services/map-service'

export const queryCardTabs = [
  {
    id: routeNames.QUERY_FISH_OVERVIEW,
    label: 'Overview',
    routeName: routeNames.QUERY_FISH_OVERVIEW,
  },
  {
    id: routeNames.QUERY_FISH_WEATHER,
    label: 'Weather',
    routeName: routeNames.QUERY_FISH_WEATHER,
  },
]

/**
 * Returns the waterbody card tab configuration, optionally including the Regions tab.
 */
export const waterbodyCardTabs = ({
  showRegionsTab,
}: {
  showRegionsTab: boolean
}) => {
  return [
    {
      id: routeNames.WATERBODY_OVERVIEW,
      label: 'Overview',
      routeName: routeNames.WATERBODY_OVERVIEW,
    },
    ...(showRegionsTab
      ? [
          {
            id: routeNames.REGIONS_TAB,
            label: 'Regions',
            routeName: routeNames.REGIONS_TAB,
          },
        ]
      : []),
    {
      id: routeNames.FISH_SPECIES,
      label: 'Species',
      routeName: routeNames.FISH_SPECIES,
    },
    {
      id: routeNames.WATERBODY_WEATHER,
      label: 'Weather',
      routeName: routeNames.WATERBODY_WEATHER,
    },
  ]
}

export const waterAccessCardTabs = [
  {
    id: routeNames.WATER_ACCESS_SITE_OVERVIEW,
    label: 'Overview',
    routeName: routeNames.WATER_ACCESS_SITE_OVERVIEW,
  },
  {
    id: routeNames.WATER_ACCESS_WEATHER,
    label: 'Weather',
    routeName: routeNames.WATER_ACCESS_WEATHER,
  },
]

/**
 * Returns the region card tab configuration, adapting routes based on whether a waterbody is present.
 */
export const regionCardTabs = (hasWaterbody: boolean) => [
  {
    id: hasWaterbody
      ? routeNames.REGION_WATERBODY_OVERVIEW
      : routeNames.REGION_OVERVIEW,
    label: 'Overview',
    routeName: hasWaterbody
      ? routeNames.REGION_WATERBODY_OVERVIEW
      : routeNames.REGION_OVERVIEW,
  },
  ...(hasWaterbody
    ? [
        {
          id: routeNames.REGION_WATERBODY_SPECIES,
          label: 'Species',
          routeName: routeNames.REGION_WATERBODY_SPECIES,
        },
      ]
    : []),
  {
    id: hasWaterbody
      ? routeNames.REGION_WATERBODY_WEATHER
      : routeNames.REGION_WEATHER,
    label: 'Weather',
    routeName: hasWaterbody
      ? routeNames.REGION_WATERBODY_WEATHER
      : routeNames.REGION_WEATHER,
  },
]

/**
 * Publishes a queryPoint event to add a point marker at the current query coordinate.
 */
export const addPoint = (origin: string) => {
  pubsub.publish('queryPoint', {
    evt: { currentTarget: { id: 'add-point' } },
    coord: queryCoord(),
    origin,
  })
}

/**
 * Publishes a queryPhoto event to add a photo at the current query coordinate.
 */
export const addPhoto = () => {
  pubsub.publish('queryPhoto', {
    evt: { currentTarget: { id: 'add-photo' } },
    coord: queryCoord(),
    origin: MARKUP_CREATE_ORIGINS.PHOTO_QUERY_CARD,
  })
}

/**
 * Navigates the user to the fish app feedback route.
 */
export const sendFeedback = (router: Router) => {
  router
    .push({
      name: routeNames.APP_FEEDBACK_FISH,
    })
    .catch(() => {})
}

/**
 *  Formats a number using compact notation (e.g. 15.2K) for values over 9999.
 * @param value - number to abbreviate (ex 15200 → "15.2K")
 */
export const abbreviateNumbers = (value: number) => {
  let notation: Intl.NumberFormatOptions['notation'] = 'standard'
  const options: Intl.NumberFormatOptions = {
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  }
  let compactFormatter
  if (value > 9999) {
    notation = 'compact'
    compactFormatter = new Intl.NumberFormat('en-US', {
      notation,
      ...options,
    })
    return compactFormatter.format(value)
  }
  compactFormatter = new Intl.NumberFormat('en-US', {
    notation,
    ...options,
  })
  return compactFormatter.format(value)
}

// Area is not supported through the "Quick Stats" logic, so some addl formatting needed
/**
 * Formats a square-meter area value into a user-facing string with the appropriate unit (acres or hectares).
 */
export const formatAreaForDisplay = (
  areaSqm: number,
  useAbbreviations: boolean = true
) => {
  const isImperial = getItem('unitType') === 'imperial'
  const toUOM = isImperial ? 'acres' : 'hectares'
  const convertedValue = getConversionFunction('sqMeters')(toUOM)(areaSqm)
  const roundedValue = round(convertedValue, 0)
  const result = abbreviateNumbers(roundedValue)
  const unit = useAbbreviations ? AreaAbbrevs[toUOM] : toUOM
  return { result, unit }
}

export const nullString = '--'

/**
 * Returns display data (stat, unit, icon) for a waterbody's maximum depth, or placeholder values if absent.
 */
export const maxDepthDisplay = (maxDepth: number | undefined | null) => {
  const isImperial = getItem('unitType') === 'imperial'
  const converted = isImperial ? metersToFeet(maxDepth) : maxDepth
  if (maxDepth) {
    return {
      stat: formatNumber(1)(converted),
      unit: isImperial ? 'ft' : 'm',
      icon: 'arrow-down',
    }
  }

  return { stat: nullString, unit: '', icon: '' }
}

/**
 * Returns display data (stat, unit, icon) for a waterbody's surface area, or placeholder values if absent.
 */
export const areaDisplay = (waterArea: number | undefined | null) => {
  if (waterArea) {
    const formattedArea = formatAreaForDisplay(waterArea)
    return {
      stat: formattedArea.result,
      unit: formattedArea.unit,
      icon: 'stat-area',
    }
  }
  return { stat: nullString, unit: '', icon: '' }
}

/**
 * Returns display data (stat, unit, icon) for the most recent fish survey year, or placeholder values if absent.
 */
export const surveyDisplay = (latestFishSurveyYear: number) => {
  if (latestFishSurveyYear) {
    return {
      stat: latestFishSurveyYear,
      unit: '',
      icon: 'binoculars',
    }
  }

  return { stat: nullString, unit: '', icon: '' }
}

/**
 * Returns the highest-priority content field available on a fishing region (summary, features, forage, or seasonal movement).
 */
export const regionContentHierarchy = (region: FishingRegion) => {
  // A region can have any of these options
  // Return them in priority order
  return (
    region.summary ??
    region.features ??
    region.forage ??
    region.seasonalFishMovement
  )
}

/**
 * Returns true if the fishing region has any displayable content.
 */
export const regionContainsContent = (region: FishingRegion) => {
  // Possible to have a valid region with no content
  // Don't show button if that is the case
  return !!regionContentHierarchy(region)
}

/**
 * Merge fish stocking data with year data
 * Data does not include years with 0-values (ex: [{2022: 100}, {2024: 200}]
 * Must fill in missing years with 0 values for graph (ex: [{2022: 100}, {2023: 0}, {2024: 200}]
 */
export const mergeDataWithYears = (stockingRecords: FishStockingRecord[]) => {
  if (!stockingRecords || stockingRecords.length === 0) {
    return { data: [], labels: [] }
  }

  const firstYear = stockingRecords[0].year
  const lastYear = stockingRecords[stockingRecords.length - 1].year

  // Calling `range` with same start/end returns empty array, so handle that case
  if (firstYear === lastYear) {
    return {
      data: [stockingRecords[0].value],
      labels: [firstYear],
    }
  }

  // `range` does not include the end value, so add 1 to lastYear
  const yearsRange = range(firstYear, lastYear + 1)

  const dataByYear = new Map(stockingRecords.map((item) => [item.year, item]))
  const mergedData = yearsRange.map((year: number): FishStockingRecord => {
    const item = dataByYear.get(year)
    return item ?? ({ year, value: 0 } as FishStockingRecord)
  })

  const dataValue = mergedData.map((data) => data.value)
  return { data: dataValue, labels: yearsRange }
}

/**
 * Formats a fish stocking date string into a human-readable month and year (e.g. "Jun, 2023").
 */
export const stockingDateDisplay = (stockingDate: string) => {
  const date = new Date(stockingDate)
  return dateFormat(date, 'mmm, yyyy')
}
